// Работа с полем composition прайс-листа (JSONB в Postgres).
// Структура JSON (PriceListComposition и типы внутри неё) — в api/priceListApi.d.ts.
// Поле tireRunFlat на этом этапе не редактируется, но при сохранении переносится
// из исходного JSON, чтобы не затереть чужие данные.

import { TIRE_OPERATIONS_56 } from '../../../core/constants/refs';
import type {
  CompositionComplex,
  CompositionPosition,
  PriceListComposition,
} from '../../api/priceListApi';

// Справочник комплексов ШМ
export const COMPLEX_REF_ID = TIRE_OPERATIONS_56;

// В форме quantity хранится строкой (как и остальные числовые поля проекта), в JSON уходит числом

export interface ComplexPositionFormValues {
  positionId: string,
  quantity: string,
  required: boolean,
  sign: string,
}

export interface ComplexFormValues {
  complexRefValueId: number,
  // позиция прайса, чья цена — множитель для колёс RunFlat; необязательное, null = не задано
  multiplierPositionId: string | null,
  positions: ComplexPositionFormValues[],
}

// Новая позиция комплекса со значениями по умолчанию
export const newPositionFormValues = (positionId: string): ComplexPositionFormValues => ({
  positionId,
  quantity: '1',
  required: true,
  sign: '+',
});

// Безопасный разбор composition: поля может не быть; прийти может строкой с JSON или уже объектом
export const parseComposition = (raw?: string | PriceListComposition | null): PriceListComposition => {
  if (!raw) {
    return {};
  }

  if (typeof raw !== 'string') {
    return raw;
  }

  try {
    const parsed = JSON.parse(raw);

    return (parsed && typeof parsed === 'object') ? parsed as PriceListComposition : {};
  } catch {
    return {};
  }
};

export const compositionToFormValues = (composition: PriceListComposition): ComplexFormValues[] =>
  (composition.complexes ?? []).map((complex) => ({
    complexRefValueId: complex.complexRefValueId,
    multiplierPositionId: complex.multiplierPositionId ?? null,
    positions: (complex.positions ?? []).map((position) => ({
      positionId: position.positionId,
      quantity: String(position.quantity ?? 1),
      required: position.required ?? true,
      sign: position.sign ?? '+',
    })),
  }));

export const formValuesToComplexes = (complexes: ComplexFormValues[]): CompositionComplex[] =>
  complexes.map((complex) => ({
    complexRefValueId: complex.complexRefValueId,
    // ключ попадает в JSON только когда множитель задан — очистка селектора убирает поле совсем
    ...(complex.multiplierPositionId ? { multiplierPositionId: complex.multiplierPositionId } : {}),
    positions: complex.positions.map((position) => ({
      positionId: position.positionId,
      quantity: Number(position.quantity),
      required: position.required,
      sign: position.sign as CompositionPosition['sign'],
    })),
  }));

// Итоговый JSON: complexes из редактора, остальные поля (tireRunFlat и т.п.) — из исходного composition
export const buildComposition = (
  complexes: ComplexFormValues[],
  original: PriceListComposition
): PriceListComposition => ({
  ...original,
  complexes: formValuesToComplexes(complexes),
});
